import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  legalLinkClass,
  legalListClass,
  LegalPageLayout,
  LegalSection,
  legalSubheadingClass,
} from "~/components/clawinstall/LegalPageLayout.tsx";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { type HeadCtxLike, identityFromHeadCtx, marketingHead } from "~/lib/clawinstall-marketing-head.ts";

const tableWrapClass = "overflow-x-auto";
const tableClass = "w-full border-collapse text-left font-sans text-body-md";
const thClass = "border-b border-outline-variant/60 py-2 pr-4 font-label-md text-label-md text-on-surface";
const tdClass = "border-b border-outline-variant/40 py-2 pr-4 align-top";

const dataCategories = [
  {
    category: "Account & identity",
    examples: "Name, email, organization, authentication identifiers",
    role: "Controller",
  },
  {
    category: "Billing",
    examples: "Subscription/customer identifiers held at our payments provider, plan, invoice history",
    role: "Controller",
  },
  {
    category: "Connection credentials",
    examples: "Encrypted OAuth tokens and API keys you supply for your Agent",
    role: "Processor",
  },
  {
    category: "Customer-directed content",
    examples: "Data the Agent reads/sends on your instruction, including prompts/content sent to LLM providers",
    role: "Processor",
  },
  {
    category: "Agent & Node telemetry",
    examples: "Health, status, resource and connectivity metrics, operational logs",
    role: "Controller",
  },
  {
    category: "Product usage",
    examples: "Dashboard/API interactions, feature usage, device and approximate location from IP",
    role: "Controller",
  },
  { category: "Support", examples: "Correspondence with our team", role: "Controller" },
  { category: "Cookies & similar", examples: "See §11", role: "Controller" },
] as const;

const subprocessors = [
  {
    name: "Stripe",
    purpose: "Payments & subscription billing (web)",
    data: "Billing/customer & subscription identifiers, payment events",
    location: "United States / EU",
  },
  {
    name: "RevenueCat",
    purpose: "Mobile subscription entitlements, synced with Stripe",
    data: "Subscriber/app-user identifiers, entitlement & purchase metadata",
    location: "United States",
  },
  {
    name: "Clerk",
    purpose: "Authentication, identity, and transactional auth email",
    data: "Account identity, session tokens, org membership",
    location: "United States",
  },
  {
    name: "AgentMail",
    purpose: "Transactional product email (e.g. installation/setup messages)",
    data: "Customer name, email address, message content",
    location: "United States",
  },
  {
    name: "DigitalOcean",
    purpose:
      "Application / control-plane hosting (Kubernetes: API, web, and cluster services for *.clawinstall.ai) and managed Valkey (Redis) cache/queues",
    data: "All controller-role data in transit and at the application tier; transient queue/cache records",
    location: "United States (region-configurable)",
  },
  {
    name: "Neon",
    purpose: "Managed PostgreSQL database",
    data: "Stored account, billing, usage data and customer-directed metadata",
    location: "United States (AWS)",
  },
  {
    name: "Hetzner Cloud",
    purpose: "Compute for customer Agent/Node provisioning",
    data: "Agent runtime state and customer-directed content processed on the Node",
    location: "Germany / EU (or selected region)",
  },
  {
    name: "Tailscale",
    purpose: "Secure mesh networking between Nodes and control plane",
    data: "Connection and device metadata",
    location: "United States",
  },
  {
    name: "OpenAI",
    purpose: "LLM inference (sole LLM provider; see §6)",
    data: "Customer-directed prompts/content",
    location: "United States",
  },
  {
    name: "Tavily",
    purpose: "Web search powering customer-initiated research workflows",
    data: "Search queries derived from customer-directed research instructions",
    location: "United States",
  },
  {
    name: "Discord",
    purpose: "Managed Discord bot applications operated for customer Agents",
    data:
      "Customer/workspace name applied to the bot application; messages the Agent exchanges in Discord servers you connect",
    location: "United States",
  },
  {
    name: "Amazon Web Services (S3)",
    purpose: "Object storage for workflow/research outputs and session transcripts",
    data: "Customer-directed content artifacts",
    location: "United States",
  },
  {
    name: "PostHog",
    purpose: "Product analytics (website, dashboard, mobile app)",
    data: "Usage events, device data, approximate location, cookie identifiers",
    location: "United States (us.i.posthog.com)",
  },
  {
    name: "Datadog",
    purpose: "Observability / distributed tracing",
    data: "Operational telemetry and trace metadata",
    location: "United States (region-configurable)",
  },
] as const;

/** Head meta tags for the Privacy Policy page (brand name + canonical host follow the request identity). */
export const privacyHead = (ctx?: HeadCtxLike) => {
  const identity = identityFromHeadCtx(ctx);
  return marketingHead({
    path: "/privacy",
    title: `Privacy Policy — ${identity.displayName}`,
    description:
      `How ${identity.displayName} collects, uses, shares, and protects personal data across the ${identity.canonicalHost} website, dashboard, and APIs.`,
    identity,
  });
};

/**
 * ClawInstall Privacy Policy page
 *
 * Renders the finalized Privacy Policy (docs/legal/clawinstall-privacy.md).
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/privacy")({
  head: privacyHead,
  component: ClawInstallPrivacyPage,
});

function ClawInstallPrivacyPage() {
  const identity = useAtomValue(clawInstallIdentityAtom);
  const name = identity.displayName;
  const privacyEmail = (
    <a href={`mailto:${identity.emails.privacy}`} className={legalLinkClass}>{identity.emails.privacy}</a>
  );
  return (
    <LegalPageLayout
      title={`${name} Privacy Policy`}
      lastUpdated="5 September 2026"
      version="1.3"
      current="/clawinstall/privacy"
      testId="privacy-page"
    >
      <section className="space-y-4">
        <p>
          <strong>{identity.legalName}</strong>, a California limited liability company{identity.dbaName === null
            ? null
            : (
              <>
                {" "}doing business as <strong>{identity.dbaName}</strong>
              </>
            )}{" "}
          (“<strong>{name}</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, “<strong>our</strong>”), operates
          the service at{" "}
          <a href={`https://${identity.canonicalHost}`} className={legalLinkClass}>{identity.canonicalHost}</a>{" "}
          (the “<strong>Service</strong>”), which provisions and manages autonomous Agent nodes that act on a customer's
          behalf. This Privacy Policy explains what personal data we process, why, the legal bases we rely on, who we
          share it with, and the rights you have over it.
        </p>
        <p>
          This policy covers the {identity.canonicalHost}{" "}
          website, dashboard, and APIs. It does not cover third-party services you choose to connect to your Agent,
          which are governed by their own privacy policies.
        </p>
      </section>

      <LegalSection title="1. Who we are and how to contact us">
        <p>
          For account, billing, and product-usage data described in this policy, the <strong>data controller</strong> is
          {" "}
          <strong>{identity.legalName}</strong>, a California limited liability company{identity.dbaName === null
            ? null
            : <>{" "}doing business as {identity.dbaName}</>}.
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>Controller:</strong> {identity.legalLine}.
          </li>
          <li>
            <strong>Privacy contact:</strong> {privacyEmail}
          </li>
          <li>
            <strong>Postal address:</strong> available on request via the privacy contact above.
          </li>
        </ul>
        <p>
          If you are in the EEA or UK, you may also contact our representative or Data Protection Officer where one has
          been appointed (see §13).
        </p>
      </LegalSection>

      <LegalSection title="2. Our role: controller vs. processor">
        <p>{name} plays two distinct data-protection roles, depending on the data:</p>
        <h3 className={legalSubheadingClass}>2.1 Where we are the controller</h3>
        <p>
          We are the <strong>controller</strong>{" "}
          for the data we collect to run our business and operate your account, including:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>Account & identity data</strong>{" "}
            — your name, email, and organization, managed through our authentication provider.
          </li>
          <li>
            <strong>Billing data</strong>{" "}
            — subscription and payment identifiers held by our payments provider, plan and invoice records.
          </li>
          <li>
            <strong>Product usage & telemetry</strong>{" "}
            — how you use the dashboard and APIs, Agent and Node operational health/telemetry, logs, and diagnostics.
          </li>
          <li>
            <strong>Support communications</strong> — messages you send us and our replies.
          </li>
        </ul>
        <p>
          For this data we decide the purposes and means of processing, and your data-subject rights (§10) run against
          us.
        </p>
        <h3 className={legalSubheadingClass}>2.2 Where we are the processor (customer-directed data)</h3>
        <p>
          The Service exists to let your Agent read from and send data to systems <strong>you</strong>{" "}
          choose and connect (for example, accounts you authorize via OAuth or API keys). For everything the Agent
          reads, generates, or sends <strong>on your instruction</strong>{" "}
          — together “<strong>customer-directed content</strong>” —{" "}
          <strong>you are the controller and we act as your processor</strong>. We process customer-directed content
          only to provide the Service and only on your documented instructions, which include:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>Connection credentials</strong>{" "}
            — OAuth tokens and API keys you provide so the Agent can act on your behalf. These are stored{" "}
            <strong>encrypted at rest</strong> and used solely to operate the connections you configure.
          </li>
          <li>
            <strong>Content the Agent handles</strong>{" "}
            — the data your Agent reads, transforms, or transmits to the third-party systems you connected, including
            any data passed to an LLM provider to fulfil your instructions (see §6).
          </li>
        </ul>
        <p>
          We do not use customer-directed content for our own purposes, do not sell it, and do not use it to train
          models. A standalone Data Processing Addendum (DPA) with Standard Contractual Clauses is available for
          customers who require one.
        </p>
      </LegalSection>

      <LegalSection title="3. The data we collect">
        <div className={tableWrapClass}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Category</th>
                <th className={thClass}>Examples</th>
                <th className={thClass}>Role</th>
              </tr>
            </thead>
            <tbody>
              {dataCategories.map((row) => (
                <tr key={row.category}>
                  <td className={tdClass}>{row.category}</td>
                  <td className={tdClass}>{row.examples}</td>
                  <td className={tdClass}>{row.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          We do not intentionally collect special-category data, and we ask that you not route special-category data
          through the Agent without an appropriate lawful basis and a DPA in place.
        </p>
      </LegalSection>

      <LegalSection title="4. How we use your data">
        <p>We use controller-role data to:</p>
        <ul className={legalListClass}>
          <li>create and secure your account and authenticate you;</li>
          <li>provision, operate, monitor, and support your Agents and Nodes;</li>
          <li>process subscriptions, payments, and invoices;</li>
          <li>understand product usage and improve the Service;</li>
          <li>communicate with you about service, security, and account matters;</li>
          <li>comply with legal obligations and enforce our terms.</li>
        </ul>
        <p>
          We use processor-role (customer-directed) data only to perform the Service on your instructions, as described
          in §2.2.
        </p>
      </LegalSection>

      <LegalSection title="5. Legal bases (GDPR / UK GDPR)">
        <p>Where the GDPR or UK GDPR applies, we rely on:</p>
        <ul className={legalListClass}>
          <li>
            <strong>Contract (Art. 6(1)(b))</strong>{" "}
            — to provide the Service, operate your account, and process payments.
          </li>
          <li>
            <strong>Legitimate interests (Art. 6(1)(f))</strong>{" "}
            — to secure, monitor, and improve the Service, prevent abuse, and run product analytics, balanced against
            your rights.
          </li>
          <li>
            <strong>Consent (Art. 6(1)(a))</strong>{" "}
            — for non-essential cookies/analytics where required (§11) and any optional communications. You may withdraw
            consent at any time.
          </li>
          <li>
            <strong>Legal obligation (Art. 6(1)(c))</strong> — to meet tax, accounting, and other legal requirements.
          </li>
        </ul>
        <p>
          For customer-directed content we process as a processor, the legal basis is determined by you as controller.
        </p>
      </LegalSection>

      <LegalSection title="6. AI / LLM provider processing">
        <p>
          The Agent uses a large language model (LLM) provider to carry out your instructions. To do so,
          customer-directed content (such as prompts and the content the Agent is working with) is transmitted to the
          LLM provider, processed to generate a response, and returned to the Agent.
        </p>
        <ul className={legalListClass}>
          <li>
            The LLM provider is <strong>OpenAI</strong>{" "}
            (processing in the United States). The Service does not currently offer per-customer provider selection or
            regional (data-residency) routing for LLM processing.
          </li>
          <li>We send to the provider only what is needed to fulfil your instruction.</li>
          <li>
            We do not permit your customer-directed content to be used to train the provider's foundation models, and we
            rely on the provider's enterprise/API terms that exclude API content from training.
          </li>
        </ul>
        <p>
          If we add LLM providers or regional processing options in the future, we will update the subprocessor table
          (§8) and give notice of the change as described there.
        </p>
      </LegalSection>

      <LegalSection title="7. Sharing and disclosure">
        <p>We share personal data only with:</p>
        <ul className={legalListClass}>
          <li>
            the <strong>subprocessors</strong> listed in §8, under contract and only as needed to provide the Service;
          </li>
          <li>
            <strong>professional advisers, auditors, and authorities</strong>{" "}
            where required by law or to protect our rights; and
          </li>
          <li>
            a <strong>successor entity</strong>{" "}
            in the event of a merger, acquisition, or asset sale, subject to this policy.
          </li>
        </ul>
        <p>
          We do <strong>not</strong>{" "}
          sell personal data, and we do not “share” it for cross-context behavioral advertising as those terms are
          defined under U.S. state privacy laws.
        </p>
        <h3 className={legalSubheadingClass}>7.1 Google user data (Google Workspace connections)</h3>
        <p>
          If you connect a Google account to your Agent, {name}{" "}
          requests access to Gmail, Google Drive, Google Sheets, Google Docs, Google Calendar and Google Contacts
          through Google's OAuth consent screen. We access that data only to perform the tasks you instruct your Agent
          to perform (for example, reading and sending mail, finding and editing files, or creating calendar events).
          The OAuth tokens Google issues are stored encrypted at rest (§2.2) and are used solely to operate the
          connection you configured.
        </p>
        <p>
          We do not use Google user data for advertising, do not sell it, do not use it to train AI models, and do not
          transfer it to third parties except to the LLM provider described in §6 as needed to fulfil your instructions,
          or as required by law. Our personnel do not read Google user data unless you ask us to for support, it is
          necessary for security purposes or to comply with applicable law, or it has been aggregated and anonymized for
          internal operations. You can revoke a Google connection from your dashboard at any time, and you can withdraw
          {name}'s access from your Google Account permissions page, which invalidates the tokens Google issued to us.
        </p>
        <p>
          <strong>
            The use of information received from Google Workspace scopes will adhere to the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              className={legalLinkClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              Google User Data Policy
            </a>
            , including the Limited Use requirements.
          </strong>
        </p>
      </LegalSection>

      <LegalSection title="8. Subprocessors">
        <p>
          The following third parties process personal data on our behalf to deliver the Service. We maintain contracts
          (including data-processing terms and, where relevant, Standard Contractual Clauses) with each. We will give
          notice of material changes to this list.
        </p>
        <div className={tableWrapClass}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Subprocessor</th>
                <th className={thClass}>Purpose</th>
                <th className={thClass}>Data processed</th>
                <th className={thClass}>Primary location</th>
              </tr>
            </thead>
            <tbody>
              {subprocessors.map((row) => (
                <tr key={row.name}>
                  <td className={tdClass}>
                    <strong>{row.name}</strong>
                  </td>
                  <td className={tdClass}>{row.purpose}</td>
                  <td className={tdClass}>{row.data}</td>
                  <td className={tdClass}>{row.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>A current list is available on request via {privacyEmail}.</p>
        <h3 className={legalSubheadingClass}>8.1 Infrastructure and internal tooling (no customer personal data)</h3>
        <p>
          We also use providers that support how we build and distribute the Service but that do <strong>not</strong>
          {" "}
          process customer personal data, and are therefore not subprocessors: <strong>Cloudflare R2</strong>{" "}
          (public distribution of Agent binaries and release manifests — public artifacts only), <strong>GitHub</strong>
          {" "}
          (source hosting, CI, and internal development automation over our own code), <strong>Linear</strong>{" "}
          (internal project management), and <strong>Slack</strong>{" "}
          (internal operational alerts containing service identifiers, not customer content). If any of these is later
          placed in the customer personal-data path, it will be added to the table above first.
        </p>
      </LegalSection>

      <LegalSection title="9. International transfers">
        <p>
          We are a global service and our subprocessors are located in the United States, the EU/EEA, and elsewhere.
          Where we transfer personal data out of the EEA or UK, we rely on appropriate safeguards, principally the
          European Commission's <strong>Standard Contractual Clauses</strong>{" "}
          (and the UK International Data Transfer Addendum), supplemented by technical and organizational measures. A
          copy of the relevant transfer mechanism is available on request.
        </p>
      </LegalSection>

      <LegalSection title="10. Your rights">
        <p>Subject to applicable law, you have the right to:</p>
        <ul className={legalListClass}>
          <li>
            <strong>access</strong> the personal data we hold about you;
          </li>
          <li>
            <strong>rectify</strong> inaccurate or incomplete data;
          </li>
          <li>
            <strong>erase</strong> your data (“right to be forgotten”);
          </li>
          <li>
            <strong>restrict</strong> or <strong>object to</strong>{" "}
            certain processing, including processing based on legitimate interests;
          </li>
          <li>
            <strong>data portability</strong> — receive your data in a structured, machine-readable format;
          </li>
          <li>
            <strong>withdraw consent</strong> at any time where processing is based on consent; and
          </li>
          <li>
            <strong>lodge a complaint</strong>{" "}
            with your supervisory authority (in the EEA/UK) or your state Attorney General.
          </li>
        </ul>
        <p>
          <strong>EEA/UK (GDPR):</strong> exercise any right above by contacting{" "}
          {privacyEmail}. You may also complain to your local Data Protection Authority.
        </p>
        <p>
          <strong>California (CCPA/CPRA) and other U.S. states:</strong>{" "}
          you have the rights to know, delete, correct, and to opt out of any “sale” or “sharing” of personal data. We
          do not sell or share personal data as defined by these laws. We do not discriminate against you for exercising
          your rights. Submit requests, including authorized-agent requests, to{" "}
          {privacyEmail}; we will verify your request against your account.
        </p>
        <p>
          Where you are exercising rights over <strong>customer-directed content</strong>{" "}
          that we process as a processor, please direct your request to the relevant customer (controller); we will
          assist them as required by our contract.
        </p>
      </LegalSection>

      <LegalSection title="11. Cookies and analytics">
        <p>The Service uses cookies and similar technologies:</p>
        <ul className={legalListClass}>
          <li>
            <strong>Strictly necessary</strong> — our authentication provider sets a session cookie (e.g.{" "}
            <code>__session</code>) required to keep you signed in. These are exempt from consent.
          </li>
          <li>
            <strong>Functional</strong> — a first-party cookie (e.g.{" "}
            <code>fm_waitlist</code>) remembers product-flow state such as waitlist participation.
          </li>
          <li>
            <strong>Analytics</strong> — <strong>PostHog</strong>{" "}
            sets cookies/identifiers to measure product usage and feature engagement. These are{" "}
            <strong>non-essential</strong>.
          </li>
        </ul>
        <p>
          <strong>Consent:</strong>{" "}
          Because non-essential analytics cookies load, visitors in the EEA and UK are asked for prior consent through a
          cookie-consent banner before analytics cookies are set, and analytics is suppressed until consent is given.
          You can withdraw consent or change your choice at any time. (The consent-banner interface is delivered
          separately from this policy.)
        </p>
        <p>We do not use cookies for cross-context behavioral advertising.</p>
      </LegalSection>

      <LegalSection title="12. Security">
        <p>We protect personal data with technical and organizational measures, including:</p>
        <ul className={legalListClass}>
          <li>
            <strong>encryption in transit</strong> (TLS) and{" "}
            <strong>encryption at rest</strong>, including encryption of stored OAuth tokens and API keys;
          </li>
          <li>
            <strong>private mesh networking</strong> (Tailscale) between Agent Nodes and our control plane;
          </li>
          <li>
            <strong>access controls and least-privilege</strong> for internal systems;
          </li>
          <li>
            <strong>monitoring, logging, and observability</strong> to detect and respond to incidents; and
          </li>
          <li>
            <strong>regular review</strong> of our subprocessors and security practices.
          </li>
        </ul>
        <p>
          No method of transmission or storage is completely secure, but we work to protect your data and will notify
          you and regulators of a qualifying breach as required by law.
        </p>
      </LegalSection>

      <LegalSection title="13. EU / UK representative and DPO">
        <p>
          Where {name}{" "}
          offers the Service to data subjects in the EEA or UK without an establishment there, we appoint a
          representative under Article 27 of the GDPR / UK GDPR. Where appointment of a Data Protection Officer is
          required under Article 37, we appoint one. Current representative/DPO contact details, where applicable, are
          published here and available via {privacyEmail}.
        </p>
      </LegalSection>

      <LegalSection title="14. Data retention">
        <p>We keep personal data only as long as necessary for the purposes in this policy:</p>
        <ul className={legalListClass}>
          <li>
            <strong>Account & identity data</strong>{" "}
            — for the life of your account and a limited period afterward to handle disputes and legal obligations.
          </li>
          <li>
            <strong>Billing records</strong> — as required by tax and accounting law (typically several years).
          </li>
          <li>
            <strong>Connection credentials & customer-directed content</strong>{" "}
            — for as long as the relevant connection or Agent is active; deleted or returned on termination in
            accordance with our processing terms.
          </li>
          <li>
            <strong>One-time setup tokens & sign-up magic links</strong>{" "}
            — these short-lived credentials expire quickly by design (minutes to hours). Once expired or used they carry
            no further function, and an automated sweep purges the expired credential within about an hour of expiry.
          </li>
          <li>
            <strong>Provisioning & installation telemetry</strong>{" "}
            — the step-by-step progress events shown while an Agent is being set up are retained for{" "}
            <strong>90 days</strong>, then deleted.
          </li>
          <li>
            <strong>Installation runtime logs</strong>{" "}
            — operational log lines emitted by the installer and management agent are retained for{" "}
            <strong>30 days</strong>, then deleted.
          </li>
          <li>
            <strong>Other telemetry and analytics</strong>{" "}
            — for a limited operational period, then deleted or aggregated.
          </li>
        </ul>
        <p>
          These windows are enforced automatically by a scheduled retention job, which runs on a regular cadence to
          remove data that has aged past the limits above. When data is no longer needed, we delete or irreversibly
          anonymize it.
        </p>
      </LegalSection>

      <LegalSection title="15. Children">
        <p>
          The Service is not directed to children under 16, and we do not knowingly collect personal data from them. If
          you believe a child has provided us personal data, contact {privacyEmail} and we will delete it.
        </p>
      </LegalSection>

      <LegalSection title="16. Changes to this policy">
        <p>
          We may update this policy from time to time. Material changes will be posted on this page with an updated
          “Last updated” date and version, and, where required, we will notify you directly.
        </p>
      </LegalSection>

      <LegalSection title="17. Contact">
        <p>
          Questions or requests about this policy or your personal data: {privacyEmail}.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
